import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import './BankingAIAnalysisModal.css';

/**
 * Banking AI Analysis Modal
 * 
 * Displays comprehensive AI banking verification analysis results
 * including verification status, confidence score, reasoning, 
 * extracted banking data, validation results, and key evidence.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Object} props.analysisData - Full AI analysis data from audit log
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onAccept - Callback when user accepts analysis
 * @param {Function} props.onDecline - Callback when user declines analysis
 */
function BankingAIAnalysisModal({ isOpen, analysisData, onClose, onAccept, onDecline }) {
    const [isProcessing, setIsProcessing] = React.useState(false);
    
    // Handle escape key - MUST be called before any conditional returns
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format confidence score as percentage
    const formatConfidenceScore = (score) => {
        return `${Math.round((score || 0) * 100)}%`;
    };

    // Get confidence badge class based on score
    const getConfidenceBadgeClass = (score) => {
        if (score >= 0.8) return 'confidence-badge-high';
        if (score >= 0.6) return 'confidence-badge-medium';
        return 'confidence-badge-low';
    };

    // Handle overlay click (close only if clicking outside modal)
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Handle accept button
    const handleAccept = async () => {
        if (!onAccept || !analysisData?.analysis_id) return;
        
        setIsProcessing(true);
        try {
            await onAccept(analysisData.analysis_id);
        } finally {
            setIsProcessing(false);
        }
    };

    // Handle decline button
    const handleDecline = async () => {
        if (!onDecline || !analysisData?.analysis_id) return;
        
        setIsProcessing(true);
        try {
            await onDecline(analysisData.analysis_id);
        } finally {
            setIsProcessing(false);
        }
    };

    // Early return AFTER all hooks
    if (!isOpen || !analysisData) {
        return null;
    }

    const isVerified = analysisData.verification_status === 'VERIFIED';
    const confidenceScore = analysisData.confidence_score || 0;
    const selectedDocuments = analysisData.selected_documents || [];
    const extractedData = analysisData.extracted_banking_data || {};
    const ibanResults = analysisData.iban_validation_results?.results || [];
    const swiftResults = analysisData.swift_validation_results?.results || [];
    const metadata = analysisData.processing_metadata || {};
    const keyEvidence = metadata.key_evidence || {};

    return (
        <div 
            className="banking-ai-modal-overlay" 
            onClick={handleOverlayClick}
        >
            <div className="banking-ai-modal">
                {/* Header */}
                <div className="banking-ai-modal-header">
                    <div className="header-content">
                        <h3 className="modal-title">Banking AI Verification Analysis</h3>
                        <span className="modal-subtitle">
                            {analysisData.partner_name && `${analysisData.partner_name} - `}
                            {formatTimestamp(analysisData.timestamp)}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="banking-ai-modal-close-btn"
                        title="Close"
                        aria-label="Close modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="banking-ai-modal-body">
                    {/* Status and Confidence Badges */}
                    <div className="banking-ai-modal-badges">
                        <div className={`badge ${isVerified ? 'status-badge-verified' : 'status-badge-unverified'}`}>
                            <span className="badge-label">Status:</span>
                            <span className="badge-value">{analysisData.verification_status}</span>
                        </div>
                        <div className={`badge ${getConfidenceBadgeClass(confidenceScore)}`}>
                            <span className="badge-label">Confidence:</span>
                            <span className="badge-value">{formatConfidenceScore(confidenceScore)}</span>
                        </div>
                        <div className="badge">
                            <span className="badge-label">Documents:</span>
                            <span className="badge-value">{selectedDocuments.length}</span>
                        </div>
                        <div className="badge">
                            <span className="badge-label">Date:</span>
                            <span className="badge-value">{formatTimestamp(analysisData.timestamp)}</span>
                        </div>
                    </div>

                    {/* Verification Reasoning */}
                    <div className="banking-ai-modal-section">
                        <h4 className="banking-ai-modal-section-title">Verification Reasoning</h4>
                        <div className="banking-ai-modal-section-content">
                            {analysisData.verification_reasoning || 'No reasoning provided.'}
                        </div>
                    </div>

                    {/* Documents Analyzed */}
                    {selectedDocuments.length > 0 && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">Documents Analyzed</h4>
                            <ul className="banking-ai-modal-document-list">
                                {selectedDocuments.map((doc, index) => (
                                    <li key={index}>{doc.filename || 'Unknown document'}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Extracted Banking Data - grouped per bank */}
                    {extractedData && Object.keys(extractedData).length > 0 && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">Extracted Banking Data</h4>
                            {(() => {
                                const bankCount = Math.max(
                                    extractedData.bank_names?.length || 0,
                                    extractedData.account_holders?.length || 0,
                                    extractedData.iban_codes?.length || 0,
                                    extractedData.swift_codes?.length || 0,
                                    extractedData.account_numbers?.length || 0
                                );
                                if (bankCount === 0) return null;
                                return Array.from({ length: bankCount }, (_, idx) => (
                                    <div key={idx} style={{
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        padding: '1rem',
                                        marginBottom: '0.75rem',
                                        backgroundColor: idx % 2 === 0 ? '#f9fafb' : '#ffffff'
                                    }}>
                                        <div style={{ fontWeight: '600', color: '#4a6b25', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                                            Bank {idx + 1}
                                        </div>
                                        <div className="banking-ai-modal-data-grid">
                                            {extractedData.account_holders?.[idx] && (
                                                <div className="banking-ai-modal-data-item">
                                                    <div className="banking-ai-modal-data-label">Account Holder</div>
                                                    <div className="banking-ai-modal-data-value">{extractedData.account_holders[idx]}</div>
                                                </div>
                                            )}
                                            {extractedData.bank_names?.[idx] && (
                                                <div className="banking-ai-modal-data-item">
                                                    <div className="banking-ai-modal-data-label">Bank Name</div>
                                                    <div className="banking-ai-modal-data-value">{extractedData.bank_names[idx]}</div>
                                                </div>
                                            )}
                                            {extractedData.account_numbers?.[idx] && (
                                                <div className="banking-ai-modal-data-item">
                                                    <div className="banking-ai-modal-data-label">Account Number</div>
                                                    <div className="banking-ai-modal-data-value">{extractedData.account_numbers[idx]}</div>
                                                </div>
                                            )}
                                            {extractedData.iban_codes?.[idx] && (
                                                <div className="banking-ai-modal-data-item">
                                                    <div className="banking-ai-modal-data-label">IBAN</div>
                                                    <div className="banking-ai-modal-data-value">{extractedData.iban_codes[idx]}</div>
                                                </div>
                                            )}
                                            {extractedData.swift_codes?.[idx] && (
                                                <div className="banking-ai-modal-data-item">
                                                    <div className="banking-ai-modal-data-label">SWIFT</div>
                                                    <div className="banking-ai-modal-data-value">{extractedData.swift_codes[idx]}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>
                    )}

                    {/* IBAN Validation Results */}
                    {ibanResults.length > 0 && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">IBAN Validation Results</h4>
                            <div className="banking-ai-modal-validation-grid">
                                {ibanResults.map((result, index) => (
                                    <div 
                                        key={index} 
                                        className={`banking-ai-modal-validation-item ${result.valid ? 'valid' : 'invalid'}`}
                                    >
                                        <div className="banking-ai-modal-validation-code">
                                            {result.iban}
                                        </div>
                                        <div className="banking-ai-modal-validation-details">
                                            <strong>Valid:</strong> {result.valid ? 'Yes' : 'No'}
                                            {result.country_name && (
                                                <span> | <strong>Country:</strong> {result.country_name}</span>
                                            )}
                                            {result.currency && (
                                                <span> | <strong>Currency:</strong> {result.currency}</span>
                                            )}
                                            {result.sepa_member && (
                                                <span> | <strong>SEPA:</strong> {result.sepa_member}</span>
                                            )}
                                            {result.source && (
                                                <span> | <strong>Source:</strong> {result.source.replace('_', ' ')}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* SWIFT Validation Results */}
                    {swiftResults.length > 0 && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">SWIFT Validation Results</h4>
                            <div className="banking-ai-modal-validation-grid">
                                {swiftResults.map((result, index) => (
                                    <div 
                                        key={index} 
                                        className={`banking-ai-modal-validation-item ${result.valid ? 'valid' : 'invalid'}`}
                                    >
                                        <div className="banking-ai-modal-validation-code">
                                            {result.swift}
                                        </div>
                                        <div className="banking-ai-modal-validation-details">
                                            <strong>Valid:</strong> {result.valid ? 'Yes' : 'No'}
                                            {result.bank_name && (
                                                <span> | <strong>Bank:</strong> {result.bank_name}</span>
                                            )}
                                            {result.city && (
                                                <span> | <strong>City:</strong> {result.city}</span>
                                            )}
                                            {result.country && (
                                                <span> | <strong>Country:</strong> {result.country}</span>
                                            )}
                                            {result.source && (
                                                <span> | <strong>Source:</strong> {result.source.replace('_', ' ')}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Key Evidence */}
                    {keyEvidence && (keyEvidence.positive_indicators || keyEvidence.negative_indicators) && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">Key Evidence</h4>
                            
                            {/* Positive Indicators */}
                            {keyEvidence.positive_indicators && keyEvidence.positive_indicators.length > 0 && (
                                <div className="banking-ai-modal-evidence">
                                    <strong style={{ fontSize: '0.875rem', color: '#047857' }}>
                                        Positive Indicators:
                                    </strong>
                                    <ul className="banking-ai-modal-evidence-list positive">
                                        {keyEvidence.positive_indicators.map((indicator, index) => (
                                            <li key={index}>{indicator}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Negative Indicators */}
                            {keyEvidence.negative_indicators && keyEvidence.negative_indicators.length > 0 && (
                                <div className="banking-ai-modal-evidence">
                                    <strong style={{ fontSize: '0.875rem', color: '#991b1b' }}>
                                        Negative Indicators:
                                    </strong>
                                    <ul className="banking-ai-modal-evidence-list negative">
                                        {keyEvidence.negative_indicators.map((indicator, index) => (
                                            <li key={index}>{indicator}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Consistency Score */}
                            {keyEvidence.consistency_score !== undefined && (
                                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                                    <strong>Consistency Score:</strong> {Math.round(keyEvidence.consistency_score * 100)}%
                                </div>
                            )}
                        </div>
                    )}

                    {/* Processing Information */}
                    {metadata && Object.keys(metadata).length > 0 && (
                        <div className="banking-ai-modal-section">
                            <h4 className="banking-ai-modal-section-title">Processing Information</h4>
                            <div className="banking-ai-modal-section-content">
                                {metadata.documents_processed !== undefined && (
                                    <div><strong>Documents Processed:</strong> {metadata.documents_processed}</div>
                                )}
                                {metadata.documents_converted !== undefined && (
                                    <div><strong>Documents Converted:</strong> {metadata.documents_converted}</div>
                                )}
                                {metadata.extraction_successful !== undefined && (
                                    <div><strong>Extraction Successful:</strong> {metadata.extraction_successful ? 'Yes' : 'No'}</div>
                                )}
                                {metadata.external_validation_completed !== undefined && (
                                    <div><strong>External Validation:</strong> {metadata.external_validation_completed ? 'Completed' : 'Not Completed'}</div>
                                )}
                                {metadata.decision_successful !== undefined && (
                                    <div><strong>AI Decision:</strong> {metadata.decision_successful ? 'Successful' : 'Failed'}</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {onAccept && onDecline && analysisData.analysis_id && (
                        <div className="banking-ai-modal-actions">
                            <button
                                onClick={handleAccept}
                                disabled={isProcessing}
                                className="banking-ai-modal-btn banking-ai-modal-btn-accept"
                                title="Accept AI Verification Result"
                            >
                                <FontAwesomeIcon icon={faCheck} style={{ marginRight: '8px' }} />
                                {isProcessing ? 'Processing...' : 'Accept Verification'}
                            </button>
                            <button
                                onClick={handleDecline}
                                disabled={isProcessing}
                                className="banking-ai-modal-btn banking-ai-modal-btn-decline"
                                title="Decline AI Verification Result"
                            >
                                <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '8px' }} />
                                {isProcessing ? 'Processing...' : 'Decline Verification'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BankingAIAnalysisModal;

